import {
    type PriceCalculationValueWithUnit,
    type BoundFileValue,
    type ParameterValueKind,
    type PriceCalculationParameterInfo,
    type ServiceDefinition,
} from "../api";
import { ResolvedParameterValue } from "./ResolvedParameterValue";

/**
 * Represents a file upload value with configurable restrictions.
 */
export class ResolvedFileValue extends ResolvedParameterValue<null, BoundFileValue> {
    readonly accept: string;
    readonly maxFileSize: number;
    readonly multiple: boolean;
    readonly help?: string;

    /**
     * Creates a new file value with accept types, max file size, and multiple flag bound to a parameter.
     *
     * @param value The file upload configuration
     */
    constructor(value: Omit<BoundFileValue, "kind">) {
        super({
            ...value,
            kind: "file",
            value: null,
        });
        this.accept =
            value.accept ??
            // eslint-disable-next-line max-len
            "image/*,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv";
        this.maxFileSize = value.maxFileSize ?? 10;
        this.multiple = value.multiple ?? true;
        this.help = value.help;
    }

    getKind(): ParameterValueKind {
        return this.kind;
    }

    /**
     * Gets the accepted file types.
     *
     * @returns The accepted MIME types
     */
    getAccept(): string {
        return this.accept;
    }

    /**
     * Gets the maximum file size in MB.
     *
     * @returns The maximum file size
     */
    getMaxFileSize(): number {
        return this.maxFileSize;
    }

    /**
     * Gets whether multiple files can be uploaded.
     *
     * @returns True if multiple files allowed
     */
    isMultiple(): boolean {
        return this.multiple;
    }

    /**
     * Gets the help text for the file upload field.
     *
     * @returns The help text or undefined if not set
     */
    getHelp(): string | undefined {
        return this.help;
    }

    getCalculationParameterParameterInfos(): PriceCalculationParameterInfo[] {
        return [];
    }

    getCalculationParameterValues(_value: null | undefined): Record<string, PriceCalculationValueWithUnit> {
        return {};
    }

    /**
     * Gets the view information for the parameter.
     * File uploads don't have a simple text representation.
     *
     * @param _value The value of the parameter (always null for files)
     * @returns Undefined as files don't have a text view
     */
    getViewText(_value: null | undefined): string | undefined {
        return undefined;
    }

    /**
     * Creates a customer-safe version of this value.
     * This value type has no pricing data to strip.
     *
     * @param _requestPricing - The pricing mode (unused - no pricing data in this type)
     * @returns This instance (no changes needed for any pricing mode)
     */
    toCustomerValue(_requestPricing: ServiceDefinition.RequestPricing): this {
        return this;
    }

    /**
     * Creates a new file value with accept types, max file size, and multiple flag.
     *
     * @param value The file upload configuration bound to a parameter
     * @returns A new file value
     */
    static create(value: Omit<BoundFileValue, "kind">): ResolvedFileValue {
        return new ResolvedFileValue(value);
    }
}
